import type { ApiErrorCode } from '@ipfabric/utils/apiErrorCodes';
import type { BaseTexture } from 'pixi.js';

export type IpfErrorType =
  | 'GENERIC'
  | 'ABSURD'
  | 'API'
  | 'INVALID_ARGUMENT'
  | 'INVALID_STATE'
  | 'NOT_IMPLEMENTED'
  | 'PIXI_TEXTURE_LOAD'
  | 'STUMBLE'
  | 'INSUFFICIENT_RIGHTS'
  | 'INVALID_CREDENTIALS';

export type IpfApiErrorObject = {
  message: string;
};

export type IpfErrorResponse = {
  code?: ApiErrorCode;
  data?: unknown;
  // array of errors returned from API (multiple failing validations etc.)
  errors?: IpfApiErrorObject[];
  message?: string;
  /** Either connection failed or API returned 5xx error with unspecified details in payload (= api completely down) */
  serverError?: boolean;
};

interface IpfErrorPayloadsByType {
  API: {
    response: IpfErrorResponse;
    url: string;
    httpCode: number;
  };
  PIXI_TEXTURE_LOAD: {
    texture: BaseTexture;
    errorEvent: Event;
  };
}

const isApiWithResponse = (
  type: IpfErrorType,
  payload: any,
): payload is IpfErrorPayloadsByType['API'] =>
  type === 'API' && payload?.response;

export class IpfError<TType extends IpfErrorType> extends Error {
  /**
   * @deprecated will be removed in greenfield. Use payload.response.code instead
   */
  code?: ApiErrorCode;

  /**
   * @deprecated will be removed in greenfield. Use payload.response.errors instead
   * array of errors returned from API (multiple failing validations etc.)
   */
  errors?: IpfApiErrorObject[];

  payload?: TType extends keyof IpfErrorPayloadsByType
    ? IpfErrorPayloadsByType[TType]
    : undefined;

  type: IpfErrorType;

  constructor(
    type: TType,
    message: string,
    ...payload: TType extends keyof IpfErrorPayloadsByType
      ? [IpfErrorPayloadsByType[TType]]
      : [undefined?]
  ) {
    if (isApiWithResponse(type, payload?.[0]) && payload[0].response?.message) {
      super(payload[0].response?.message);
    } else {
      super(message);
    }
    this.name = 'IpfError';
    this.type = type || 'GENERIC';
    this.payload = payload[0] as any;

    if (isApiWithResponse(type, payload?.[0])) {
      if (payload[0]?.response?.code) {
        this.code = payload[0]?.response?.code;
      }

      if (payload[0]?.response?.errors) {
        this.errors = payload[0]?.response?.errors;
      }
    }
  }
}

export class AbsurdIpfError extends IpfError<'ABSURD'> {
  constructor(_absurdValue: never, message: string) {
    super('ABSURD', message);
  }
}

export const isIpfError: {
  (x: unknown): x is IpfError<IpfErrorType>;
  <TType extends IpfErrorType>(x: unknown, type: TType): x is IpfError<TType>;
} = (x: any, type?: IpfErrorType): x is IpfError<IpfErrorType> =>
  x?.name === 'IpfError' && (type === undefined || x.type === type);
